import React from 'react';
import useHorizontalScroll from '../useHorizontalScroll';
import CarouselDots from '../CarouselDots/CarouselDots';
import { useDotPager } from '../useDotPager';
import { useReachEnd } from '../useReachEnd';
import './EdgeFadeScroller.scss';

interface EdgeFadeScrollerProps {
  children: React.ReactNode;
  className?: string;
  /** Fired once per arrival when the row is scrolled near its right edge, so a
   *  caller can append the next page. Re-arms after the row grows. */
  onReachEnd?: () => void;
  /** Pager dots under the row, the same ones the mobile carousel shows. Opt-in:
   *  the edge fade alone carries rows that are browsed, not paged. Adds a
   *  wrapper element, so rows without dots keep exactly the markup they had. */
  showDots?: boolean;
}

// Shared horizontal scroll row: NO scrollbar, with a JS-toggled edge-fade gradient
// that signals more content off either side (could-buy picks, building card-rows…).
// Drag/swipe + vertical-wheel → horizontal scrolling come from useHorizontalScroll;
// canLeft/canRight drive the --fade-left/--fade-right mask.
const EdgeFadeScroller: React.FC<EdgeFadeScrollerProps> = ({
  children,
  className,
  onReachEnd,
  showDots = false,
}) => {
  const { ref, canLeft, canRight, handlers } = useHorizontalScroll();
  // Count, not the array: `children` is a fresh array every render, and using it
  // as a dep would tear down and re-attach the observers on each one.
  const pager = useDotPager(ref, showDots, [React.Children.count(children)]);
  useReachEnd(ref, onReachEnd, children);

  const cls =
    'edge-fade-scroller' +
    (canLeft ? ' edge-fade-scroller--fade-left' : '') +
    (canRight ? ' edge-fade-scroller--fade-right' : '') +
    (className ? ` ${className}` : '');
  const row = (
    <div
      ref={ref}
      className={cls}
      onScroll={showDots ? pager.handleScroll : undefined}
      {...handlers}
    >
      {children}
    </div>
  );
  if (!showDots) return row;
  // The shell stays mounted whether or not the dots show: swapping the row's
  // parent as overflow comes and goes would remount it and lose scroll position.
  return (
    <div className="edge-fade-scroller-shell">
      {row}
      {pager.hasOverflow && (
        <CarouselDots
          count={pager.slideCount}
          progress={pager.progress}
          onSeek={pager.scrollToFraction}
        />
      )}
    </div>
  );
};

export default EdgeFadeScroller;
